import React, { useRef } from 'react'
import { FormControl, Popover, PopoverActions } from '@mui/material'
import { useCreateTechnologyMutation, useGetTechnologyTypesQuery } from '../../store/redux/reducers/technologiesSlice'
import { useForm, FieldValues } from 'react-hook-form'
import ImageUploadButton from '../Button/ImageUploadButton'
import { NewTechnologyFormValues } from '../../store/types'
import SelectTechnologyType from '../Form/SelectTechnologyType'
import { useNotification } from '../../store/redux/hooks'
import { useTranslation } from 'react-i18next'
import { createErrorMessage, readFileAsync, stopPropagationForKey } from '../../store/utils'
import { LoadingButton } from '@mui/lab'
import TextInput from '../Form/Inputs/TextInput'

type AddTechnologyPopupProps = {
  open: boolean
  setOpen: React.Dispatch<React.SetStateAction<boolean>>
  anchorEl: HTMLDivElement | null
}

const AddTechnologyPopup = ({ open, setOpen, anchorEl }: AddTechnologyPopupProps) => {
  // TRANSLATION
  const { t } = useTranslation()

  // NOTIFICATIONS
  const { displayNotification } = useNotification()

  // TECHNOLOGIES' TYPES
  const { data: technologyTypes } = useGetTechnologyTypesQuery({})

  // FORM
  const {
    register,
    handleSubmit,
    control,
    watch,
    reset,
    resetField,
    formState: { isSubmitting, isSubmitSuccessful },
  } = useForm<NewTechnologyFormValues>()

  const [createTechnology] = useCreateTechnologyMutation()

  // POPUP
  const handleClose = () => {
    reset()
    setOpen(false)
  }

  const onSubmit = (data: FieldValues) => {
    readFileAsync(data.logo, async (result, type) => {
      try {
        await createTechnology({
          name: data.name,
          logo: btoa(result as string),
          type: data.type.value,
          extension: type !== undefined ? type.split('/')[1].toUpperCase() : undefined,
        }).unwrap()
        displayNotification({ message: t('page.adminsPage.alerts.technology.create.success'), type: 'success' })
        handleClose()
      } catch (error) {
        let message = `${t('page.adminsPage.alerts.technology.create.error')}: ${createErrorMessage(error)}`
        displayNotification({
          message: message,
          type: 'error',
        })
      }
    })
  }

  const ref = useRef<PopoverActions>(null)

  return (
    <Popover
      action={ref}
      open={open}
      onClose={() => handleClose()}
      anchorEl={anchorEl}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'center',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'center',
      }}
    >
      <FormControl
        component="form"
        onSubmit={handleSubmit(onSubmit)}
        onKeyDown={e => stopPropagationForKey(e, 'Tab')}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          gap: 2,
          p: 2,
        }}
      >
        <ImageUploadButton
          name="logo"
          label={t('page.adminsPage.form.logo')}
          register={register}
          watch={watch}
          resetField={resetField}
          clearImage={false}
          setClearImage={() => {}}
          popoverRef={ref}
        />

        <TextInput
          control={control}
          name="name"
          required
          label={t('page.adminsPage.form.name')}
          rules={{ required: t('page.adminsPage.form.nameRequired') }}
        />

        <SelectTechnologyType
          control={control}
          options={technologyTypes}
          required={true}
          label={t('page.adminsPage.form.type')}
        />

        <LoadingButton
          variant="contained"
          type="submit"
          fullWidth
          loading={isSubmitting || isSubmitSuccessful}
          disabled={isSubmitting || isSubmitSuccessful}
        >
          {t('components.button.saveTechnology')}
        </LoadingButton>
      </FormControl>
    </Popover>
  )
}

export default AddTechnologyPopup